/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { CAGroup } from 'src/app/model/control-access';
import { DatePipe } from '@angular/common';
import { CAWeekDay, CAWeeklySchedule } from 'src/app/model/control-access';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectGroups, selectGroupsPagination, selectGroupsMaxData, selectGroupModify, selectWeeklyScheduleModify, selectSpecialPlanningModify, selectCurrentDeviceStatus, selectBoardUsersTaskRunning } from 'src/app/store/device/device.selector';
import { ControlAccessMaxData, ItemsPagination } from 'src/app/model/api/data-rest';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';
import { GROUP_DEFAULT_SORT_COLUMN, GROUP_DEFAULT_SORT_COLUMN_DIRECTION, GROUP_PAGE_DEFAULT_SIZE, GROUP_PAGE_SIZE_OPTIONS, GROUP_TABLE_HEADER } from 'src/app/conf/control-access';
import { ControlAccessGroupEasyDialogDialogComponent } from '../control-access-group-easy-dialog/control-access-group-easy-dialog.component';
import { DeviceStatus } from 'src/app/model/device-status';

@Component({
	selector: 'app-control-access-group-easy-list',
	templateUrl: './control-access-group-easy-list.component.html',
	providers: [ConfirmationService, DialogService]
})

// Access Control RBE
export class ControlAccessGroupEasyListComponent implements OnInit, OnDestroy {
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	groups$: Observable<CAGroup[] | undefined>;
	groupsSub: Subscription | undefined;
	groupsPagination$: Observable<ItemsPagination | undefined>;
	groupsPaginationSub: Subscription | undefined;
	groupsMaxData$: Observable<ControlAccessMaxData | undefined>;
	groupsMaxDataSub: Subscription | undefined;
	groupsModify$: Observable<boolean | undefined>;
	groupsModifySub: Subscription | undefined;
	weeklyScheduleModify$: Observable<boolean | undefined>;
	weeklyScheduleModifySub: Subscription | undefined;
	specialPlanningModify$: Observable<boolean | undefined>;
	specialPlanningModifySub: Subscription | undefined;
	taskRunning$: Observable<boolean>;
	taskRunningSub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;
	deviceStatus: DeviceStatus;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;

	searchFilter: FormControl = new FormControl('');
	filterSub: Subscription;
	filter: '';

	groupColumns = GROUP_TABLE_HEADER;
	groupPagingSub: Subscription;
	groupPageSortSub: Subscription;
	groupPaging: PaginationParams = {
		size: GROUP_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: GROUP_DEFAULT_SORT_COLUMN,
		order: GROUP_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	groupPageSize: number = GROUP_PAGE_DEFAULT_SIZE;
	groupPageSizeOptions = GROUP_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	groupsMaxSizeMemorySub: Subscription;
	groupsMemorySize = 1;
	groupsMaxMemorySize = 1;

	groupsLoading: boolean;
	groups: CAGroup[];

	online: boolean;
	currentSite: Site;
	taskRunning: boolean;

	groupConfirmDeleteSub: Subscription;

	dateTransform: DatePipe;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	opened = false;

	// Design
	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.groups$ = this.store.select(selectGroups);
		this.groupsPagination$ = this.store.select(selectGroupsPagination);
		this.groupsMaxData$ = this.store.select(selectGroupsMaxData);
		this.groupsModify$ = this.store.select(selectGroupModify);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
		this.weeklyScheduleModify$ = this.store.select(selectWeeklyScheduleModify);
		this.specialPlanningModify$ = this.store.select(selectSpecialPlanningModify);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.taskRunning$ = this.store.select(selectBoardUsersTaskRunning);
	}

	ngOnInit(): void {
		const currentLang = this.translate.currentLang;

		this.dateTransform = new DatePipe(currentLang);
		this.subscribeAll();
		this.onResize();
	}

	subscribeAll(): void {
		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.dispatchGetGroups();
		});
		this.dispatchGetGroups();
		this.groupsSub = this.groups$.subscribe(groups => {
			this.groups = groups;
			this.groupsLoading = false;
		});

		this.groupsPaginationSub = this.groupsPagination$
			.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			}
			);

		this.groupsMaxSizeMemorySub = this.groupsMaxData$
			.pipe(filter(p => !!p))
			.subscribe(groupsMemorySizeMaxData => {
				this.groupsMemorySize = groupsMemorySizeMaxData.Current;
				this.groupsMaxMemorySize = groupsMemorySizeMaxData.Max;
			}
			);

		this.groupsModifySub = this.groupsModify$
			.pipe(filter(groupsModify => !!groupsModify))
			.subscribe(() => {
				this.store.dispatch(DeviceActions.getGroupsAll());
				this.dispatchGetGroups();
			});


		this.weeklyScheduleModifySub = this.weeklyScheduleModify$
			.pipe(filter(weeklyScheduleModify => !!weeklyScheduleModify))
			.subscribe(() => {
				this.dispatchGetGroups();
			});
		this.specialPlanningModifySub = this.specialPlanningModify$
			.pipe(filter(specialPlanningModify => !!specialPlanningModify))
			.subscribe(() => {
				this.dispatchGetGroups();
			});
		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
		});
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetGroups();
			});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceStatus => {
			this.deviceStatus = deviceStatus;
			if (this.deviceStatus !== undefined) {
				this.online = deviceStatus.Online;
			}
		});
		this.taskRunningSub = this.taskRunning$.subscribe(taskRunning => {
			this.taskRunning = taskRunning;
		});
	}

	ngOnDestroy(): void {
		this.groupsModifySub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.specialPlanningModifySub?.unsubscribe();
		this.weeklyScheduleModifySub?.unsubscribe();
		this.groupsMaxSizeMemorySub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.taskRunningSub?.unsubscribe();
	}


	loadGroups(event: TableLazyLoadEvent) {
		this.groupsLoading = true;

		this.groupPaging = pageSortChange(this.groupPaging, event);

		this.dispatchGetGroups();
	}

	dispatchGetGroups(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getGroups({
				filter: this.filter,
				sort: this.groupPaging.sort, order: this.groupPaging.order, size: this.groupPaging.size, page: this.groupPaging.page
			}));
		}
	}

	isControlAccess(): boolean {
		//return (this.deviceModel !== undefined && (this.deviceModel.Id === EDeviceModel.MTMACONNECT) && this.tabIndex === 9); // TAB INDEX FOR MTMACONNECT CALLLOG IS 9
		return true; // TAB INDEX FOR MTMACONNECT IS 7 tab only for mtma

	}

	isSelectTab(): boolean {
		return this.isControlAccess() && this.opened;
	}



	addGroup(): void {
		this.store.dispatch(DeviceActions.resetGroupForm());
		this.ref = this.dialogService.open(ControlAccessGroupEasyDialogDialogComponent, {
			header: this.translate.instant('ADD_GROUP'),
			width: '75vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editGroup(group: CAGroup): void {
		this.store.dispatch(DeviceActions.resetGroupForm());
		this.ref = this.dialogService.open(ControlAccessGroupEasyDialogDialogComponent, {
			header: this.translate.instant('EDIT_GROUP'),
			width: '75vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: { group },
		});
	}

	deleteGroup(group: CAGroup): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_GROUP'),
			message: this.translate.instant('DELETE_GROUP_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteGroup({ groupId: group.Id }));
			},
			reject: () => {
			}
		});
	}

	daysOfWeekSchedule(weeklySchedule: CAWeeklySchedule): CAWeekDay[] {
		const currentWeekDays = [
			{ Value: '0', Name: 'MON', Enable: false },
			{ Value: '1', Name: 'TUE', Enable: false },
			{ Value: '2', Name: 'WED', Enable: false },
			{ Value: '3', Name: 'THU', Enable: false },
			{ Value: '4', Name: 'FRI', Enable: false },
			{ Value: '5', Name: 'SAT', Enable: false },
			{ Value: '6', Name: 'SUN', Enable: false },
		];
		const ppp = [];
		let value = weeklySchedule.Weekdays;
		for (let i = 6; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				ppp.unshift(i.toString());
				currentWeekDays[i].Enable = true;
			}
			value = value % (2 ** i);
		}
		return currentWeekDays;
	}

	getNumWeeklySchedule(group: CAGroup): number {
		if (group.WeeklySchedules === undefined) {
			return 0;
		}
		return group.WeeklySchedules.length;
	}


	getNumSpecialPlanning(group: CAGroup): number {
		if (group.SpecialPlannings === undefined) {
			return 0;
		}
		return group.SpecialPlannings.length;
	}

	getValueMemory(): number {
		return Math.ceil((this.groupsMemorySize / this.groupsMaxMemorySize) * 100);
	}

	togglePanel(opened: boolean): void {
		this.opened = opened;
		this.dispatchGetGroups();
	}

	getWidthLabelWs(weeklySchedules: CAWeeklySchedule[]): string {
		let numChar = 5;
		for (const ws of weeklySchedules) {
			if (ws.Name.length > numChar) {
				numChar = ws.Name.length;
			}
		}
		return 'width: ' + numChar * 9 + 'px;';

	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}
	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}
}